import React, { useContext, useCallback, useMemo, useState } from 'react';
import {
  Textarea,
  Field,
  tokens,
  makeStyles
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { MAX_LOCATIONS, MAX_LOCATION_LENGTH } from './utils/constants';
import { useBulkLocationsSearchContext } from './bulk-locations-search-provider';

const useStyles = makeStyles({
  lineCount: {
    color: tokens.colorNeutralForeground3,
    float: 'right',
  },
  textArea: {
    maxHeight: '800px',
  },
});

const EnterBulkLocationsTextarea = () => {
  const classes = useStyles();
  const i18n = useContext(I18nContext);
  const {
    setSearchTerms, setError, error, setInputText, inputText,
  } = useBulkLocationsSearchContext();

  const [linesCount, setLinesCount] = useState(0);

  const parseLocations = useCallback(input => input
    .split('\n')
    .map(line => line.trim())
    .filter(line => line !== ''), []);

  const validateLocations = useCallback((locations) => {
    if (locations.length > MAX_LOCATIONS) {
      return setError(i18n.getString(_TL_('You’ve entered {{count}} locations. The max. is {{maxCount}} locations. Please remove the appropriate amount.'), { count: locations.length, maxCount: MAX_LOCATIONS }));
    }

    const hasInvalidLocation = locations.some(location => location.length > MAX_LOCATION_LENGTH);
    if (hasInvalidLocation) {
      return setError(i18n.getString(_TL_('Please enter one location per line. Additionally, the max. number of characters for each location is {{maxLength}}.'), { maxLength: MAX_LOCATION_LENGTH }));
    }
    return setError('');
  }, [i18n, setError]);

  const validationProps = useMemo(() => {
    if (error.length > 0) {
      return {
        validationState: 'error',
        validationMessage: error,
      };
    }
    return {
      validationState: 'none',
      validationMessage: i18n.getString(_TL_('Enter one location per line (country/region, state/province, city, or ZIP/postal code). The max. is {{maxCount}} locations.'), { maxCount: MAX_LOCATIONS }),
    };
  }, [error, i18n]);

  const handleInputChange = useCallback(
    (e) => {
      const newText = e.target.value;
      setInputText(newText);

      const locations = parseLocations(newText);
      setLinesCount(locations.length);
      validateLocations(locations);
      setSearchTerms(locations);
    },
    [parseLocations, setInputText, setSearchTerms, validateLocations]
  );

  return (
    <Field
      label={
        <>
          <span className={classes.lineCount}>{i18n.getString(_TL_('Line count: {{count}}'), { count: linesCount })}</span>
          {i18n.getString(_TL_('Locations'))}
        </>
      }
      value={inputText}
      onChange={handleInputChange}
      {...validationProps}
    >
      <Textarea id="bulk-locations-textarea" textarea={{ className: classes.textArea }} resize="vertical" rows="20" placeholder={i18n.getString(_TL_('E.g. United States'))} />
    </Field>
  );
};

export default EnterBulkLocationsTextarea;

